// ─── GameHistoryPage.jsx ──────────────────────────────────────────────────────
import { useState, useEffect, useRef, useCallback } from "react";
import { Trophy, Coins, Zap, ArrowUpRight, Loader2 } from "lucide-react";
import { theme, injectTheme } from "../assets/theme";
import { api } from "../services/api";

const ROOM_ICON = {
  bronze: { Icon: Coins, bg: "rgba(217,119,6,0.15)", color: "#d97706" },
  silver: { Icon: Zap, bg: "rgba(167,139,250,0.15)", color: "#a78bfa" },
  gold: { Icon: Trophy, bg: "rgba(250,204,21,0.15)", color: "#facc15" },
};

function money(n) {
  return `${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ETB`;
}

function dayLabel(dateStr) {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(d, today)) return "TODAY";
  if (sameDay(d, yesterday)) return "YESTERDAY";
  return d
    .toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    .toUpperCase();
}

// Groups an already-sorted (newest first) flat list into day buckets,
// merging with whatever buckets already exist so "load more" appends cleanly.
function groupByDay(games) {
  const map = new Map();
  const order = [];
  for (const g of games) {
    const label = dayLabel(g.playedAt);
    if (!map.has(label)) {
      map.set(label, { label, profit: 0, games: [] });
      order.push(label);
    }
    const bucket = map.get(label);
    bucket.games.push(g);
    bucket.profit += g.prize - g.bet;
  }
  return order.map((label) => map.get(label));
}

function SummaryCard({ totalWinnings, gamesPlayed, loading }) {
  return (
    <div
      style={{
        borderRadius: theme.radius.xl,
        background:
          "linear-gradient(135deg, rgba(30,20,10,0.95) 0%, rgba(20,14,6,0.98) 100%)",
        border: "1px solid rgba(249,115,22,0.18)",
        padding: "20px 22px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
        <span
          style={{
            fontSize: "9px",
            fontFamily: theme.font.bodyFamily,
            fontWeight: 600,
            color: theme.color.textDim,
            textTransform: "uppercase",
            letterSpacing: "0.18em",
          }}
        >
          Total Winnings
        </span>
        <span
          style={{
            fontFamily: theme.font.family,
            fontWeight: 900,
            fontSize: "24px",
            color: theme.color.textPrimary,
          }}
        >
          {loading ? "—" : money(totalWinnings)}
        </span>
      </div>
      <div
        style={{
          width: "1px",
          height: "44px",
          background: theme.color.borderDivide,
          margin: "0 8px",
        }}
      />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "5px",
          alignItems: "flex-end",
        }}
      >
        <span
          style={{
            fontSize: "9px",
            fontFamily: theme.font.bodyFamily,
            fontWeight: 600,
            color: theme.color.textDim,
            textTransform: "uppercase",
            letterSpacing: "0.18em",
          }}
        >
          Games Played
        </span>
        <span
          style={{
            fontFamily: theme.font.family,
            fontWeight: 900,
            fontSize: "30px",
            color: theme.color.accentLight,
            textShadow: `0 0 20px ${theme.color.accentGlow}`,
          }}
        >
          {loading ? "—" : gamesPlayed}
        </span>
      </div>
    </div>
  );
}

function ResultBadge({ win }) {
  return (
    <span
      style={{
        fontSize: "9px",
        fontFamily: theme.font.bodyFamily,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.10em",
        padding: "3px 9px",
        borderRadius: theme.radius.full,
        background: win ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)",
        border: `1px solid ${win ? "rgba(34,197,94,0.30)" : "rgba(239,68,68,0.30)"}`,
        color: win ? "#22c55e" : "#ef4444",
      }}
    >
      {win ? "Win" : "Loss"}
    </span>
  );
}

function GameCard({ game }) {
  const meta = ROOM_ICON[game.roomType] ?? ROOM_ICON.bronze;
  const { Icon } = meta;
  const time = new Date(game.playedAt).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div
      style={{
        borderRadius: theme.radius.lg,
        background: theme.color.bgCard,
        border: `1px solid ${theme.color.borderCard}`,
        padding: "14px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <span
        style={{
          position: "absolute",
          left: 0,
          top: "20%",
          bottom: "20%",
          width: "3px",
          borderRadius: "0 3px 3px 0",
          background: game.won
            ? "linear-gradient(to bottom,#22c55e,#16a34a)"
            : "linear-gradient(to bottom,#ef4444,#b91c1c)",
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "12px",
          paddingLeft: "6px",
        }}
      >
        <div
          style={{
            width: "42px",
            height: "42px",
            borderRadius: theme.radius.md,
            background: meta.bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={20} color={meta.color} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              fontFamily: theme.font.family,
              fontWeight: 900,
              fontSize: "13px",
              color: theme.color.textPrimary,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              marginBottom: "5px",
            }}
          >
            {game.name}
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span
              style={{
                fontFamily: theme.font.family,
                fontWeight: 700,
                fontSize: "15px",
                color: theme.color.textSub,
                display: "flex",
                alignItems: "center",
                gap: "3px",
              }}
            >
              {money(game.bet)}
              <ArrowUpRight
                size={12}
                color={game.won ? "#22c55e" : "#ef4444"}
              />
            </span>
            <ResultBadge win={game.won} />
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: "4px",
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontSize: "9px",
              fontFamily: theme.font.bodyFamily,
              fontWeight: 600,
              color: theme.color.textDim,
              textTransform: "uppercase",
            }}
          >
            {game.won ? "Prize" : "Result"}
          </span>
          <span
            style={{
              fontFamily: theme.font.family,
              fontWeight: 900,
              fontSize: "14px",
              color: game.won ? theme.color.textPrimary : theme.color.textMuted,
            }}
          >
            {game.won ? money(game.prize) : "—"}
          </span>
          <span style={{ fontSize: "11px", color: theme.color.textMuted }}>
            {time}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function GameHistoryCont() {
  const [games, setGames] = useState([]);
  const [summary, setSummary] = useState({ totalWinnings: 0, gamesPlayed: 0 });
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const sentinelRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    injectTheme();
  }, []);

  const loadFirstPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [historyRes, summaryRes] = await Promise.all([
        api.getGameHistory(),
        api.getGameHistorySummary(),
      ]);
      setGames(historyRes.games);
      setCursor(historyRes.nextCursor);
      setHasMore(historyRes.hasMore);
      setSummary(summaryRes);
    } catch (err) {
      setError(err.message || "Could not load your game history.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !cursor) return;
    setLoadingMore(true);
    try {
      const res = await api.getGameHistory(cursor);
      setGames((prev) => [...prev, ...res.games]);
      setCursor(res.nextCursor);
      setHasMore(res.hasMore);
    } catch (err) {
      console.warn("Could not load more history:", err.message);
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, hasMore, loadingMore]);

  // Infinite scroll: fire loadMore when the sentinel div at the bottom
  // scrolls into view inside the scrollable container.
  useEffect(() => {
    if (!sentinelRef.current || !scrollRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { root: scrollRef.current, rootMargin: "200px", threshold: 0 },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loadMore]);

  const groups = groupByDay(games);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        background: theme.color.bgSurface,
        overflow: "hidden",
        boxShadow: theme.shadow.phone,
        display: "flex",
        flexDirection: "column",
        height: "100vh",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 20px 14px",
        }}
      >
        <h1
          style={{
            fontFamily: theme.font.family,
            fontWeight: 900,
            fontSize: "20px",
            color: theme.color.textPrimary,
            letterSpacing: "0.02em",
            textTransform: "uppercase",
          }}
        >
          Game History
        </h1>
      </header>

      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "0 16px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          scrollbarWidth: "none",
          paddingBottom: "100px",
        }}
      >
        <SummaryCard
          totalWinnings={summary.totalWinnings}
          gamesPlayed={summary.gamesPlayed}
          loading={loading}
        />

        {loading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "40px 0",
              color: theme.color.textMuted,
            }}
          >
            <Loader2 size={22} className="spin" />
          </div>
        )}

        {!loading && error && (
          <div
            style={{
              textAlign: "center",
              padding: "30px 16px",
              color: theme.color.textMuted,
            }}
          >
            <p style={{ marginBottom: "10px" }}>{error}</p>
            <button
              onClick={loadFirstPage}
              style={{
                padding: "8px 16px",
                borderRadius: theme.radius.md,
                background: theme.color.accent,
                border: "none",
                color: "#fff",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && games.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "40px 16px",
              color: theme.color.textMuted,
            }}
          >
            No games played yet — jump into a room to get started!
          </div>
        )}

        {!loading &&
          !error &&
          groups.map((group) => (
            <div
              key={group.label}
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "2px 0",
                }}
              >
                <span
                  style={{
                    fontSize: "10px",
                    fontFamily: theme.font.bodyFamily,
                    fontWeight: 700,
                    color: theme.color.textDim,
                    textTransform: "uppercase",
                    letterSpacing: "0.18em",
                  }}
                >
                  {group.label}
                </span>
                <span
                  style={{
                    fontSize: "12px",
                    fontFamily: theme.font.bodyFamily,
                    fontWeight: 700,
                    color: group.profit >= 0 ? "#22c55e" : "#ef4444",
                  }}
                >
                  {group.profit >= 0 ? "+" : ""}
                  {money(group.profit)}
                </span>
              </div>
              {group.games.map((g) => (
                <GameCard key={g.id} game={g} />
              ))}
            </div>
          ))}

        {/* Sentinel: when this scrolls into view, loadMore() fires automatically */}
        {!loading && hasMore && (
          <div
            ref={sentinelRef}
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "12px 0",
            }}
          >
            {loadingMore ? (
              <Loader2
                size={18}
                color={theme.color.textMuted}
                className="spin"
              />
            ) : (
              <button
                onClick={loadMore}
                style={{
                  fontSize: "12px",
                  color: theme.color.textMuted,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Load more
              </button>
            )}
          </div>
        )}

        {!loading && !hasMore && games.length > 0 && (
          <p
            style={{
              textAlign: "center",
              fontSize: "11px",
              color: theme.color.textDim,
              padding: "8px 0",
            }}
          >
            You've reached the end of your history.
          </p>
        )}
      </div>
    </div>
  );
}
